import React, { useState } from "react";
import { PhoneIcon, LockIcon, LogInIcon } from "lucide-react";
import { loginDeliveryPartner } from "../services/auth.Service";
import { startDeliverySession } from "../utils/deliverySession";
import { scheduleAccessTokenRefresh } from "../utils/apiConfig";
import PasswordResetInfoModal from "./PasswordResetInfoModal";

interface LoginProps {
  onLogin: (username: string) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  
const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError("");
  setIsLoading(true);

  try {
    const { access, refresh, user } = await loginDeliveryPartner(phone, password);

    // Store tokens
    localStorage.setItem("access", access);
    localStorage.setItem("refresh", refresh);
    // Keep token for backward compatibility
    localStorage.setItem("token", access);
    startDeliverySession();
    scheduleAccessTokenRefresh();

    // Store user profile
    localStorage.setItem("profile", JSON.stringify(user));
    localStorage.setItem("user", JSON.stringify(user));
    
    // Store partner info
    if (user.partner) {
      localStorage.setItem("employeeId", user.partner.id.toString());
      localStorage.setItem("partnerId", user.partner.id.toString());
      localStorage.setItem("storeId", user.partner.store_id.toString());
      localStorage.setItem("storeName", user.partner.store_name);
    }
    
    // Store user info
    localStorage.setItem("userId", user.id.toString());
    localStorage.setItem("phoneNumber", user.phone);
    localStorage.setItem("fullName", user.full_name);
    localStorage.setItem("roleName", user.role);

    // Login complete - APIs will be called in App component
    onLogin(user.full_name || user.phone);
  } catch (err: any) {
    setError(err.message || "Something went wrong");
  } finally {
    setIsLoading(false);
  }
};



  return (
    <div className="min-h-screen flex flex-col bg-orange-50">
      <header className="bg-orange-600 text-white py-4 px-4 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <img
              src="/Deliveryapplogo.png"
              alt="Genda Phool Logo"
              className="w-10 h-10 rounded-full object-cover mr-3"
            />
            <h1 className="text-xl font-bold">Genda Phool</h1>
          </div>
          <div className="text-sm bg-orange-700 px-3 py-1 rounded-full">
            Delivery App
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-center mb-6">
            <img
              src="/Deliveryapplogo.png"
              alt="Genda Phool Logo"
              className="w-24 h-24 object-contain"
            />
          </div>
          <h2 className="text-2xl font-bold text-center text-orange-800 mb-6">
            Delivery Partner Login
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-orange-800 text-sm font-medium mb-2">
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <PhoneIcon size={18} className="text-orange-400" />
                </div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-amber-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="+919876543210"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-orange-800 text-sm font-medium mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockIcon size={18} className="text-orange-400" />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-amber-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter your password"
                />
              </div>
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => setShowResetPasswordModal(true)}
                  className="text-sm font-medium text-orange-700 hover:text-orange-900 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 rounded-md flex items-center justify-center"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Logging in...
                </span>
              ) : (
                <span className="flex items-center">
                  <LogInIcon size={18} className="mr-2" />
                  Login 
                </span>
              )}
            </button>
          </form>
        </div>
      </div>

      <PasswordResetInfoModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
      />
    </div>
  );
};

export default Login;
